import { apiService } from './apiService';
import { API_CONFIG } from '@/config/apiConfig';
import { Table, Order, OrderItem } from '@/types';
import { generateGuid } from './orderApiService';

interface MesaAbertaItem {
  produtoID?: number;
  ProdutoID?: number;
  descricao?: string;
  Descricao?: string;
  quantidade?: number;
  Quantidade?: number;
  totalFinal?: number;
  TotalFinal?: number;
  status?: string;
  Status?: string;
  observacao?: string;
  Observacao?: string;
  idPedidoLocal?: string;
  IdPedidoLocal?: string;
}

interface MesaPedidoErp {
  vendaID?: number;
  IdentificadorVendaPDV?: string;
  identificadorVendaPDV?: string;
  numeroComanda?: string;
  NumeroComanda?: string;
  status?: string;
  Status?: string;
  statusMesa?: string;
  StatusMesa?: string;
  dataCriacao?: string;
  DataCriacao?: string;
  totalFinal?: number;
  TotalFinal?: number;
  cliente?: string;
  Cliente?: string;
  documentoCliente?: string;
  DocumentoCliente?: string;
  vendedor?: string;
  Vendedor?: string;
  itens?: MesaAbertaItem[];
  Itens?: MesaAbertaItem[];
}

interface MesaAbertaErp {
  numeroMesa?: string;
  NumeroMesa?: string;
  identificadorMesa?: string;
  IdentificadorMesa?: string;
  statusMesa?: string;
  StatusMesa?: string;
  quantidadePedidos?: number;
  QuantidadePedidos?: number;
  totalAberto?: number;
  TotalAberto?: number;
  totalPago?: number;
  TotalPago?: number;
  totalPendente?: number;
  TotalPendente?: number;
  pedidos?: MesaPedidoErp[];
  Pedidos?: MesaPedidoErp[];
}

interface MesasAbertasResponse {
  success?: boolean;
  mesas?: MesaAbertaErp[];
  message?: string;
}

type ErpStatusMesa = 'aberta' | 'fechada' | 'cancelada';
type ErpPreparoStatus = 'aguardando' | 'empreparo' | 'pronto' | 'entregueaogarcom' | 'cancelado';

const normalizeErpStatusMesa = (value?: unknown): ErpStatusMesa | undefined => {
  if (value == null) return undefined;

  if (typeof value === 'number') {
    switch (value) {
      case 1:
        return 'aberta';
      case 2:
        return 'fechada';
      case 3:
        return 'cancelada';
      default:
        return undefined;
    }
  }

  const text = String(value).trim().toLowerCase();
  if (!text) return undefined;

  // aceita tanto enum serializado ("Aberta") quanto numero em string ("1")
  if (text === '1') return 'aberta';
  if (text === '2') return 'fechada';
  if (text === '3') return 'cancelada';

  if (text.includes('aberta')) return 'aberta';
  if (text.includes('fechada')) return 'fechada';
  if (text.includes('cancel')) return 'cancelada';

  return undefined;
};

const normalizeErpPreparoStatus = (value?: unknown): ErpPreparoStatus | undefined => {
  if (value == null) return undefined;
  const text = String(value).trim().toLowerCase();
  if (!text) return undefined;

  if (text.includes('aguard')) return 'aguardando';
  if (text.includes('em') && text.includes('preparo')) return 'empreparo';
  if (text.includes('pronto')) return 'pronto';
  if (text.includes('entregue')) return 'entregueaogarcom';
  if (text.includes('cancel')) return 'cancelado';

  return undefined;
};

const safeNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseMesaNumber = (value?: string): number | null => {
  if (!value) return null;
  const digits = value.match(/\d+/);
  if (digits) {
    const parsed = Number(digits[0]);
    if (Number.isFinite(parsed)) return parsed;
  }
  const direct = Number(value);
  return Number.isFinite(direct) ? direct : null;
};

const parseDate = (value?: string): Date => {
  if (!value) return new Date();
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

const mapErpPreparoStatusToApp = (value?: unknown): OrderItem['status'] => {
  const normalized = normalizeErpPreparoStatus(value);
  switch (normalized) {
    case 'empreparo':
      return 'preparing';
    case 'pronto':
      return 'ready';
    case 'entregueaogarcom':
      return 'delivered';
    case 'cancelado':
      return 'cancelled';
    case 'aguardando':
    default:
      return 'pending';
  }
};

const mapPedidoItem = (pedidoId: string, item: MesaAbertaItem, index: number): OrderItem => {
  const produtoIdNumber = safeNumber(item.produtoID ?? item.ProdutoID ?? 0, 0);

  const quantity = safeNumber(item.quantidade ?? item.Quantidade, 0);
  const total = safeNumber(item.totalFinal ?? item.TotalFinal, 0);
  const price = quantity > 0 ? total / quantity : total;

  const produtoId = (produtoIdNumber > 0 ? produtoIdNumber : index).toString();
  const descricao = item.descricao ?? item.Descricao ?? `Item ${index + 1}`;

  const rawItemStatus = item.status ?? item.Status ?? 'Aguardando';

  const idPedidoLocal = item.idPedidoLocal ?? item.IdPedidoLocal;

  return {
    id: `${pedidoId}-item-${index}`,
    IdPedidoLocal: idPedidoLocal,
    productId: produtoId,
    product: {
      id: produtoId,
      name: descricao,
      description: descricao,
      price,
      categoryId: 'erp',
      erpProductId: produtoIdNumber > 0 ? produtoIdNumber : undefined,
    },
    quantity: quantity || 1,
    notes: item.observacao ?? item.Observacao,
    status: mapErpPreparoStatusToApp(rawItemStatus),
  };
};

const normalizePedido = (mesaId: string, mesaNumber?: number) => (pedido: MesaPedidoErp, index: number): Order[] => {
  const pedidoId = pedido.vendaID?.toString() ?? pedido.identificadorVendaPDV ?? pedido.IdentificadorVendaPDV ?? `${mesaId}-pedido-${index}`;
  const createdAt = parseDate(pedido.dataCriacao ?? pedido.DataCriacao);
  const itensOrigem = pedido.itens ?? pedido.Itens ?? [];
  const items = itensOrigem.map((item, itemIndex) => mapPedidoItem(pedidoId, item, itemIndex));

  const normalizedStatusMesa = normalizeErpStatusMesa(pedido.statusMesa ?? pedido.StatusMesa);
  const normalizedStatusPedido = (pedido.status ?? pedido.Status ?? '').toString().toLowerCase();

  let orderStatus: Order['status'] = 'open';

  // prioridade: statusMesa (novo)
  if (normalizedStatusMesa === 'cancelada') {
    orderStatus = 'cancelled';
  } else if (normalizedStatusMesa === 'fechada') {
    orderStatus = 'archived';
  } else {
    // fallback para o status antigo do pedido
    if (normalizedStatusPedido === 'faturado' || normalizedStatusPedido === 'paid') {
      orderStatus = 'paid';
    } else if (normalizedStatusPedido === 'finalizado' || normalizedStatusPedido === 'closed') {
      orderStatus = 'archived';
    } else if (normalizedStatusPedido === 'cancelado' || normalizedStatusPedido === 'cancelled') {
      orderStatus = 'cancelled';
    }
  }

  const externalId = pedido.identificadorVendaPDV ?? pedido.IdentificadorVendaPDV ?? undefined;

  // Se o ERP consolidou a venda, os itens vêm com `IdPedidoLocal` (GUID) para separar subpedidos.
  // Caso não venha, cai no comportamento antigo (um pedido só).
  const groups = new Map<string, OrderItem[]>();
  for (const item of items) {
    const key = (item.IdPedidoLocal && String(item.IdPedidoLocal).trim()) || pedidoId;
    const existing = groups.get(key);
    if (existing) existing.push(item);
    else groups.set(key, [item]);
  }

  // Se não vierem itens mas houver total no payload, cria um placeholder em um único pedido.
  const totalFromPayload = safeNumber(pedido.totalFinal ?? pedido.TotalFinal, 0);
  if (items.length === 0 && totalFromPayload > 0) {
    groups.set(pedidoId, [{
      id: `${pedidoId}-item-placeholder`,
      productId: '0',
      product: {
        id: '0',
        name: 'Consumo',
        description: 'Itens não detalhados',
        price: totalFromPayload,
        categoryId: 'erp',
      },
      quantity: 1,
      status: 'pending',
    }]);
  }

  return Array.from(groups.entries()).map(([localOrderId, groupItems]) => {
    const totalFromItems = groupItems.reduce((sum, it) => sum + (it.product.price * it.quantity), 0);
    const totalAmount = totalFromItems || (items.length === 0 ? totalFromPayload : 0);

    return {
      id: localOrderId,
      tableId: mesaId,
      tableNumber: mesaNumber,
      items: groupItems,
      status: orderStatus,
      type: 'dine-in',
      createdAt,
      updatedAt: createdAt,
      totalAmount,
      customerName: pedido.cliente ?? pedido.Cliente,
      document: pedido.documentoCliente ?? pedido.DocumentoCliente,
      observations: pedido.vendedor ?? pedido.Vendedor,
      syncStatus: 'synced',
      erpId: pedido.vendaID,
      externalId,
    };
  });
};

const mapMesaToTable = (mesa: MesaAbertaErp, index: number): Table => {
  const mesaId = mesa.identificadorMesa || mesa.IdentificadorMesa || mesa.numeroMesa || mesa.NumeroMesa || generateGuid();
  const numeroMesa = parseMesaNumber(mesa.numeroMesa ?? mesa.NumeroMesa) ?? index;
  const pedidos = (mesa.pedidos ?? mesa.Pedidos ?? []).flatMap(normalizePedido(mesaId, numeroMesa));

  const statusMesa = normalizeErpStatusMesa(mesa.statusMesa ?? mesa.StatusMesa);

  // Quando o ERP mandar o status da mesa, ele prevalece.
  // Caso n?o venha, mant?m a regra antiga baseada nos pedidos.
  let mesaStatus: Table['status'];
  if (statusMesa === 'cancelada' || statusMesa === 'fechada') {
    mesaStatus = 'available';
  } else if (statusMesa === 'aberta') {
    mesaStatus = 'occupied';
  } else {
    const hasActiveOrders = pedidos.some(order => order.status === 'open' || order.status === 'paid');
    mesaStatus = hasActiveOrders ? 'occupied' : 'available';
  }

  const openedAt = pedidos.length
    ? pedidos.reduce((earliest, order) => order.createdAt < earliest ? order.createdAt : earliest, pedidos[0].createdAt)
    : undefined;

  return {
    id: mesaId,
    number: numeroMesa,
    name: mesa.numeroMesa ?? mesa.NumeroMesa,
    status: mesaStatus,
    seats: 4,
    orders: pedidos,
    timeOpened: openedAt,
    createdAt: openedAt,
  };
};

export const tablesService = {
  async getOpenTablesFromErp(): Promise<Table[]> {
    API_CONFIG.initialize();

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const isTablesListPage = currentPath === '/tables';

    // Regra de ouro: NUNCA chamar MesasAbertas fora da tela de listagem.
    // A tela de detalhe da mesa (/tables/:id) e outras telas n�o podem disparar essa chamada.
    if (!isTablesListPage) {
      return [];
    }

    try {
      const endpoint = API_CONFIG.endpoints.orders.mesasAbertas || '/api/Vendas/MesasAbertas';
      const response = await apiService.get<MesasAbertasResponse>(endpoint);

      if (!response?.success || !Array.isArray(response.mesas)) {
        return [];
      }

      return response.mesas.map((mesa, index) => mapMesaToTable(mesa, index + 1000));
    } catch (error) {
      console.warn('N�o foi poss�vel carregar mesas abertas do ERP.', error);
      return [];
    }
  },
};
